import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import ctaSection01 from '../../assets/icons/partners/cta_section_01.svg';
import ctaSection02 from '../../assets/icons/partners/cta_section_02.svg';
import ctaSection03 from '../../assets/icons/partners/cta_section_03.svg';
import ctaSection04 from '../../assets/icons/partners/cta_section_04.svg';
import ctaSection05 from '../../assets/icons/partners/cta_section_05.svg';
import ctaSection06 from '../../assets/icons/partners/cta_section_06.svg';
import ctaSection07 from '../../assets/icons/partners/cta_section_07.svg';
import { PillButton, Reveal, Section, SectionInner } from '../section/common';
import { useScrollReveal, useTypingText } from '../../hooks/useScrollReveal';

const CTA_TITLE_TEXT = `어떤 인플루언서가\n우리 브랜드에 딱 맞을지,\n지금 바로 확인해 보세요`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-14px);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const CtaWrapper = styled(Section)`
  position: relative;
  overflow: hidden;
  background: #ffffff;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 420px;
  }
`;

const DecorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 780px;
  background: #ffffff;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: auto;
    min-height: 420px;
    max-height: 560px;
  }
`;

const CtaInner = styled(SectionInner)`
  position: absolute;
  inset: 0;
  z-index: 1;
  padding: 240px 0;
  text-align: center;
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 80px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ContentBlock = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  pointer-events: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 28px;
    padding: 60px 20px;
  }
`;

const CtaTitle = styled.h2`
  color: #111;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 54px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
  letter-spacing: -1.35px;
  white-space: pre-line;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 28px;
    letter-spacing: -0.7px;
  }
`;

const blink = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
`;

const TypingCursor = styled.span`
  display: inline-block;
  width: 3px;
  height: 0.95em;
  margin-left: 4px;
  background: #111111;
  vertical-align: -0.08em;
  animation: ${blink} 0.8s step-end infinite;
`;

const DecorIcon = styled.img<{
  $animation: 'float' | 'rotate';
  $duration: number;
  $top?: string;
  $right?: string;
  $bottom?: string;
  $left?: string;
  $width: number;
  $mobileTop?: string;
  $mobileRight?: string;
  $mobileBottom?: string;
  $mobileLeft?: string;
  $mobileWidth: number;
  $hideOnMobile?: boolean;
}>`
  position: absolute;
  width: ${({ $width }) => $width}px;
  height: auto;
  pointer-events: none;
  user-select: none;
  top: ${({ $top }) => $top ?? 'auto'};
  right: ${({ $right }) => $right ?? 'auto'};
  bottom: ${({ $bottom }) => $bottom ?? 'auto'};
  left: ${({ $left }) => $left ?? 'auto'};
  animation: ${({ $animation }) => ($animation === 'float' ? float : rotate)}
    ${({ $duration }) => $duration}s ease-in-out infinite;
  ${({ $animation }) =>
    $animation === 'rotate' &&
    css`
      animation-timing-function: linear;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ $mobileWidth }) => $mobileWidth}px;
    top: ${({ $mobileTop, $top }) => $mobileTop ?? $top ?? 'auto'};
    right: ${({ $mobileRight, $right }) => $mobileRight ?? $right ?? 'auto'};
    bottom: ${({ $mobileBottom, $bottom }) =>
      $mobileBottom ?? $bottom ?? 'auto'};
    left: ${({ $mobileLeft, $left }) => $mobileLeft ?? $left ?? 'auto'};
    display: ${({ $hideOnMobile }) => ($hideOnMobile ? 'none' : 'block')};
  }
`;

type DecorItem = {
  src: string;
  alt: string;
  animation: 'float' | 'rotate';
  duration: number;
  width: number;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  mobileWidth: number;
  mobileTop?: string;
  mobileRight?: string;
  mobileBottom?: string;
  mobileLeft?: string;
  hideOnMobile?: boolean;
};

const DECOR_ITEMS: DecorItem[] = [
  {
    src: ctaSection01,
    alt: '',
    animation: 'rotate',
    duration: 24,
    width: 210,
    top: '10%',
    right: '25%',
    mobileWidth: 125,
    mobileTop: '4%',
    mobileRight: '-8%',
  },
  {
    src: ctaSection02,
    alt: '',
    animation: 'rotate',
    duration: 20,
    width: 182,
    bottom: '24%',
    left: '24%',
    mobileWidth: 140,
    mobileBottom: '-3%',
    mobileLeft: '-6%',
  },
  {
    src: ctaSection03,
    alt: '',
    animation: 'rotate',
    duration: 16,
    width: 60,
    top: '45%',
    left: '20%',
    mobileWidth: 44,
    mobileTop: '38%',
    mobileLeft: '2%',
    hideOnMobile: true,
  },
  {
    src: ctaSection03,
    alt: '',
    animation: 'rotate',
    duration: 18,
    width: 60,
    top: '32%',
    right: '22%',
    mobileWidth: 0,
    hideOnMobile: true,
  },
  {
    src: ctaSection04,
    alt: '',
    animation: 'rotate',
    duration: 14,
    width: 51,
    top: '16%',
    left: '30%',
    mobileWidth: 50,
    mobileTop: '2%',
    mobileRight: 'auto',
    mobileLeft: '3%',
  },
  {
    src: ctaSection05,
    alt: '',
    animation: 'float',
    duration: 3.2,
    width: 40,
    bottom: '30%',
    right: '32%',
    mobileWidth: 24,
    mobileBottom: '40%',
    mobileRight: '10%',
  },
  {
    src: ctaSection06,
    alt: '',
    animation: 'rotate',
    duration: 14,
    width: 51,
    top: '26%',
    left: '36%',
    mobileWidth: 32,
    mobileTop: '23%',
    mobileRight: 'auto',
    mobileLeft: '14%',
  },
  {
    src: ctaSection07,
    alt: '',
    animation: 'float',
    duration: 3.6,
    width: 94,
    bottom: '15%',
    right: '15%',
    mobileWidth: 80,
    mobileBottom: '10%',
    mobileRight: '-4%',
  },
];

const CtaSection: React.FC = () => {
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollReveal();
  const { displayed, isComplete } = useTypingText(CTA_TITLE_TEXT, isVisible);

  return (
    <CtaWrapper>
      <DecorContainer aria-hidden>
        {DECOR_ITEMS.map((item, index) => (
          <DecorIcon
            key={index}
            src={item.src}
            alt={item.alt}
            $animation={item.animation}
            $duration={item.duration}
            $width={item.width}
            $top={item.top}
            $right={item.right}
            $bottom={item.bottom}
            $left={item.left}
            $mobileWidth={item.mobileWidth}
            $mobileTop={item.mobileTop}
            $mobileRight={item.mobileRight}
            $mobileBottom={item.mobileBottom}
            $mobileLeft={item.mobileLeft}
            $hideOnMobile={item.hideOnMobile ?? false}
          />
        ))}
      </DecorContainer>
      <CtaInner ref={ref}>
        <Reveal $visible={isVisible}>
          <ContentBlock>
            <CtaTitle>
              {displayed}
              {!isComplete && <TypingCursor aria-hidden />}
            </CtaTitle>
            <PillButton
              $variant="black"
              type="button"
              onClick={() => navigate('/contents')}
            >
              콘텐츠 더보기 →
            </PillButton>
          </ContentBlock>
        </Reveal>
      </CtaInner>
    </CtaWrapper>
  );
};

export default CtaSection;
